import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
  buscarConveniosAgenda,
  buscarProcedimentosAgenda,
  buscarValoresProcediConv,
} from 'services/agenda';
import {
  atualizarFaturamento,
  excluirFaturamento,
  faturarAtividade,
  mensagemFaturamento,
} from 'services/atividades';
import useSWR from 'swr';

const SWR_STATIC_OPTIONS = {
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
  revalidateIfStale: false,
  dedupingInterval: 600_000,
};

function parseNumber(val) {
  if (val === null || val === undefined || val === '') return null;
  const num = Number(String(val).replace(',', '.'));
  return Number.isNaN(num) ? null : num;
}

const COD_REALIZOU_HOLTER = 5;
const COD_REALIZOU_MAPA = 2;

function realizouFixoDoProcedimento(codProcedi) {
  const cod = Number(codProcedi);
  if (cod === 3) return COD_REALIZOU_HOLTER;
  if (cod === 4 || cod === 25) return COD_REALIZOU_MAPA;
  return null;
}

function valorJaGravado(inicial) {
  const bruto = inicial?.VALOR ?? inicial?.VLRLAUDO;
  if (bruto === undefined || bruto === null || bruto === '') return null;
  return String(bruto);
}

export function useFaturamento({ show, faturamentoInicial, codFunc, nomeFunc, onSucesso, onHide }) {
  const { data: convenios = [], isLoading: loadingConvenios } = useSWR(
    show ? 'agenda-convenios' : null,
    buscarConveniosAgenda,
    SWR_STATIC_OPTIONS
  );

  const { data: procedimentos = [], isLoading: loadingProcedimentos } = useSWR(
    show ? 'agenda-procedimentos' : null,
    buscarProcedimentosAgenda,
    SWR_STATIC_OPTIONS
  );

  const { data: valoresProcediConv = [], isLoading: loadingValores } = useSWR(
    show ? 'atividades-valores-convenio' : null,
    buscarValoresProcediConv,
    SWR_STATIC_OPTIONS
  );

  const [modo, setModo] = useState('INSERIR');
  const [codPac, setCodPac] = useState('');
  const [codAgenda, setCodAgenda] = useState('');
  const [medConveniado, setMedConveniado] = useState({ codMed: '', nome: '' });
  const [medSolicitou, setMedSolicitou] = useState({ codMed: '', nome: '' });
  const [medRealizou, setMedRealizou] = useState({ codMed: '', nome: '' });
  const [convenio, setConvenio] = useState('');
  const [procedimento, setProcedimento] = useState('');
  const [guia, setGuia] = useState('');
  const [valor, setValor] = useState('');
  const [faturarEcg, setFaturarEcg] = useState(false);
  const [guiaEcg, setGuiaEcg] = useState('');
  const [convenioEcg, setConvenioEcg] = useState('');
  const [valorEcg, setValorEcg] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [valorAviso, setValorAviso] = useState(null);

  const obterValorProcedimento = useCallback(
    (codProc, codConv) => {
      if (!codProc || !codConv || !valoresProcediConv.length) return null;
      const reg = valoresProcediConv.find(
        (v) => String(v.codProcedi) === String(codProc) && String(v.codConv) === String(codConv)
      );
      return reg?.valor ? String(reg.valor) : null;
    },
    [valoresProcediConv]
  );

  // Sincronização quando o Modal abre/carrega
  useEffect(() => {
    if (!show || !faturamentoInicial) return;

    const ehEdicao = Boolean(
      faturamentoInicial.ID_FATURAMENTO ||
        faturamentoInicial.CODFATURA ||
        faturamentoInicial.FATURADO
    );
    setModo(ehEdicao ? 'EDITAR' : 'INSERIR');

    // Chaves de Associação
    setCodPac(String(faturamentoInicial.CODPAC || faturamentoInicial.codPac || ''));
    setCodAgenda(String(faturamentoInicial.CODAGENDA || faturamentoInicial.codAgenda || ''));

    // Resolução Flexível dos Médicos
    const codMedPadrao = String(
      faturamentoInicial.CODMED ||
        faturamentoInicial.CODMEDICO ||
        faturamentoInicial.CODMED_CONVENIADO ||
        ''
    );
    const nomeMedPadrao =
      faturamentoInicial.NOMEMED ||
      faturamentoInicial.NOMEMED_CONVENIADO ||
      faturamentoInicial.CODINOME ||
      '';

    setMedConveniado({
      codMed: String(faturamentoInicial.CODMED_CONVENIADO || codMedPadrao),
      nome: faturamentoInicial.NOMEMED_CONVENIADO || nomeMedPadrao,
    });

    setMedSolicitou({
      codMed: String(faturamentoInicial.CODMED_SOLICITOU || faturamentoInicial.CODENCAMINHOU || ''),
      nome: faturamentoInicial.NOMEMED_SOLICITOU || '',
    });

    const codProcedi = faturamentoInicial.CODPROCEDI;
    const realizouFixo = realizouFixoDoProcedimento(codProcedi);

    const codMedRealizou =
      realizouFixo != null
        ? String(realizouFixo)
        : String(
            faturamentoInicial.CODMED_REALIZOU || faturamentoInicial.CODREALIZOU || codMedPadrao
          );

    const nomeMedRealizou =
      realizouFixo != null ? '' : faturamentoInicial.NOMEMED_REALIZOU || nomeMedPadrao;

    setMedRealizou({
      codMed: codMedRealizou,
      nome: nomeMedRealizou,
    });

    const codConv = String(faturamentoInicial.CODCONV || '');
    const codProc = String(faturamentoInicial.CODPROCEDI || '');
    const faturarEcgInicial = Boolean(faturamentoInicial.FATURAR_ECG || Number(codProc) === 2);
    const codConvEcg = String(faturamentoInicial.CODCONV_ECG || (faturarEcgInicial ? codConv : ''));

    setConvenio(codConv);
    setProcedimento(codProc);
    setFaturarEcg(faturarEcgInicial);
    setGuia(faturamentoInicial.GUIA ? String(faturamentoInicial.GUIA) : '');
    setConvenioEcg(codConvEcg);
    setGuiaEcg(
      faturamentoInicial.GUIA_ECG
        ? String(faturamentoInicial.GUIA_ECG)
        : String(faturamentoInicial.GUIA || '')
    );
    setError(null);

    // Na edição o valor já faturado permanece. A tabela de preço vale na inclusão
    // e quando o usuário troca procedimento ou convênio.
    const valorEncontrado = obterValorProcedimento(codProc, codConv);
    const valorFaturado = valorJaGravado(faturamentoInicial);
    if (ehEdicao && valorFaturado) {
      setValor(valorFaturado);
      setValorAviso(null);
    } else if (valorEncontrado) {
      setValor(valorEncontrado);
      setValorAviso(null);
    } else if (codProc && codConv) {
      setValor(
        faturamentoInicial.VALOR || faturamentoInicial.VLRLAUDO
          ? String(faturamentoInicial.VALOR || faturamentoInicial.VLRLAUDO)
          : ''
      );
      setValorAviso('nao_encontrado');
    } else {
      setValor(
        faturamentoInicial.VALOR || faturamentoInicial.VLRLAUDO
          ? String(faturamentoInicial.VALOR || faturamentoInicial.VLRLAUDO)
          : ''
      );
      setValorAviso(null);
    }

    // Resolução do valor do ECG
    const convAlvoEcg = codConvEcg || codConv;
    if (faturarEcgInicial && convAlvoEcg) {
      const valorEcgEncontrado = obterValorProcedimento('2', convAlvoEcg);
      setValorEcg(
        valorEcgEncontrado ||
          (faturamentoInicial.VALOR_ECG
            ? String(faturamentoInicial.VALOR_ECG)
            : String(faturamentoInicial.VLRLAUDO || ''))
      );
    } else {
      setValorEcg(faturamentoInicial.VALOR_ECG ? String(faturamentoInicial.VALOR_ECG) : '');
    }
  }, [show, faturamentoInicial, obterValorProcedimento]);

  const aplicarValor = useCallback(
    (codProc, codConv) => {
      if (!codProc || !codConv) {
        setValor('');
        setValorAviso(null);
        return;
      }
      const valorEncontrado = obterValorProcedimento(codProc, codConv);
      if (valorEncontrado) {
        setValor(valorEncontrado);
        setValorAviso(null);
      } else {
        setValor('');
        setValorAviso('nao_encontrado');
      }
    },
    [obterValorProcedimento]
  );

  const handleProcedimentoChange = useCallback(
    (cod) => {
      setProcedimento(cod);
      const fixo = realizouFixoDoProcedimento(cod);
      if (fixo != null) {
        setMedRealizou({ codMed: String(fixo), nome: '' });
      }
      if (cod !== '1') {
        setFaturarEcg(false);
        setConvenioEcg('');
        setGuiaEcg('');
        setValorEcg('');
      }
      aplicarValor(cod, convenio);
    },
    [aplicarValor, convenio]
  );

  const handleConvenioChange = useCallback(
    (cod) => {
      setConvenio(cod);
      aplicarValor(procedimento, cod);

      if (faturarEcg && (!convenioEcg || convenioEcg === convenio)) {
        setConvenioEcg(cod);
        const valorEcgEncontrado = obterValorProcedimento('2', cod);
        setValorEcg(valorEcgEncontrado || '');
      }
    },
    [aplicarValor, convenio, convenioEcg, faturarEcg, obterValorProcedimento, procedimento]
  );

  const handleConvenioEcgChange = useCallback(
    (cod) => {
      setConvenioEcg(cod);
      if (!cod) {
        setValorEcg('');
        return;
      }
      const valorEcgEncontrado = obterValorProcedimento('2', cod);
      setValorEcg(valorEcgEncontrado || '');
    },
    [obterValorProcedimento]
  );

  const handleFaturarEcgChange = useCallback(
    (checked) => {
      setFaturarEcg(checked);
      if (checked) {
        const convEcgAtivo = convenioEcg || convenio;
        setConvenioEcg(convEcgAtivo);
        setGuiaEcg((prev) => prev || guia);
        const valorEcgEncontrado = obterValorProcedimento('2', convEcgAtivo);
        setValorEcg(valorEcgEncontrado || '');
      } else {
        setConvenioEcg('');
        setGuiaEcg('');
        setValorEcg('');
      }
    },
    [convenio, convenioEcg, guia, obterValorProcedimento]
  );

  const executarAcao = useCallback(
    async (acaoForçada) => {
      const acao = acaoForçada || modo;

      const realizouFixo = realizouFixoDoProcedimento(procedimento);
      const ehEcgAtivo = acao === 'INSERIR' && String(procedimento) === '1' && faturarEcg;

      if (acao !== 'EXCLUIR') {
        if (!procedimento) return toast.warning('Selecione o procedimento.');
        if (!convenio) return toast.warning('Selecione o convênio.');
        if (realizouFixo == null && parseNumber(medRealizou.codMed) == null) {
          return toast.warning('Selecione o médico que realizou.');
        }
        if (ehEcgAtivo && !convenioEcg && !convenio) {
          return toast.warning('Selecione o convênio do ECG.');
        }
      }

      setIsSubmitting(true);
      setError(null);

      const idFaturamento = parseNumber(
        faturamentoInicial?.ID_FATURAMENTO || faturamentoInicial?.CODFATURA
      );

      const codigoFuncionarioLogado =
        acao === 'EDITAR'
          ? (parseNumber(faturamentoInicial?.CODFUNC) ?? parseNumber(codFunc))
          : (parseNumber(codFunc) ?? parseNumber(faturamentoInicial?.CODFUNC));

      const payload = {
        idFaturamento,
        codAgenda: parseNumber(codAgenda) || parseNumber(faturamentoInicial?.CODAGENDA),
        observacao: faturamentoInicial?.OBSERVACAO,
        codPac: parseNumber(codPac) || parseNumber(faturamentoInicial?.CODPAC),
        codMedConveniado:
          parseNumber(medConveniado.codMed) ||
          parseNumber(faturamentoInicial?.CODMEDICO) ||
          parseNumber(faturamentoInicial?.CODMED),
        codMedSolicitou: parseNumber(medSolicitou.codMed),
        codMedRealizou: realizouFixo ?? parseNumber(medRealizou.codMed),
        codConvenio: parseNumber(convenio) || parseNumber(faturamentoInicial?.CODCONV),
        codConvenioEcg: ehEcgAtivo
          ? parseNumber(convenioEcg) ||
            parseNumber(convenio) ||
            parseNumber(faturamentoInicial?.CODCONV)
          : null,
        codProcedimento: parseNumber(procedimento) || parseNumber(faturamentoInicial?.CODPROCEDI),
        guia: guia ? String(guia) : null,
        valor: parseNumber(valor),
        faturarEcg: ehEcgAtivo,
        guiaEcg: ehEcgAtivo && guiaEcg ? String(guiaEcg) : null,
        valorEcg: ehEcgAtivo ? parseNumber(valorEcg) : null,
        codFunc: codigoFuncionarioLogado,
        nomeFunc: nomeFunc || '',
        dataFatura:
          faturamentoInicial?.DATAAGENDA ||
          faturamentoInicial?.DATALAUDO ||
          new Date().toISOString(),
      };

      try {
        let res;
        if (acao === 'INSERIR') {
          res = await faturarAtividade(payload);
        } else if (acao === 'EDITAR') {
          res = await atualizarFaturamento(payload);
        } else if (acao === 'EXCLUIR') {
          res = await excluirFaturamento(idFaturamento);
        }

        if (res?.ok) {
          toast.success(res.mensagem || 'Operação realizada com sucesso!');
          onSucesso?.();
          onHide?.();
        } else {
          const msg = res?.mensagem || 'Operação não realizada.';
          toast.warning(msg);
          setError(msg);
        }
      } catch (err) {
        const msg = mensagemFaturamento(err, 'Erro interno ao processar requisição.');
        toast.error(msg);
        setError(msg);
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      codAgenda,
      codFunc,
      codPac,
      convenio,
      convenioEcg,
      faturarEcg,
      faturamentoInicial,
      guia,
      guiaEcg,
      medConveniado.codMed,
      medRealizou.codMed,
      medSolicitou.codMed,
      modo,
      nomeFunc,
      onHide,
      onSucesso,
      procedimento,
      valor,
      valorEcg,
    ]
  );

  const options = useMemo(
    () => ({
      convenios,
      procedimentos,
      valoresProcediConv,
      isLoading: loadingConvenios || loadingProcedimentos || loadingValores,
    }),
    [
      convenios,
      procedimentos,
      valoresProcediConv,
      loadingConvenios,
      loadingProcedimentos,
      loadingValores,
    ]
  );

  const form = useMemo(
    () => ({
      codPac,
      setCodPac,
      codAgenda,
      setCodAgenda,
      medConveniado,
      setMedConveniado,
      medSolicitou,
      setMedSolicitou,
      medRealizou,
      setMedRealizou,
      convenio,
      handleConvenioChange,
      setConvenio,
      procedimento,
      handleProcedimentoChange,
      setProcedimento,
      guia,
      setGuia,
      guiaEcg,
      setGuiaEcg,
      convenioEcg,
      handleConvenioEcgChange,
      setConvenioEcg,
      valor,
      setValor,
      valorEcg,
      setValorEcg,
      faturarEcg,
      setFaturarEcg: handleFaturarEcgChange,
    }),
    [
      codPac,
      codAgenda,
      medConveniado,
      medSolicitou,
      medRealizou,
      convenio,
      handleConvenioChange,
      procedimento,
      handleProcedimentoChange,
      guia,
      guiaEcg,
      convenioEcg,
      handleConvenioEcgChange,
      valor,
      valorEcg,
      faturarEcg,
      handleFaturarEcgChange,
    ]
  );

  return {
    modo,
    options,
    form,
    isSubmitting,
    error,
    valorAviso,
    realizouTravado: realizouFixoDoProcedimento(procedimento) != null,
    executarAcao,
  };
}

export default useFaturamento;
